/* eslint-disable default-param-last */
import { ColorTuple, hsluvToRgb } from 'hsluv';
import { hasOwnProperty } from 'utils/objects';
import { getRandomHexadecimal } from 'utils/randomUtils';

import { okhslToSrgb, okhsvToSrgb } from './okLab';

const toI = (n: number) => Math.floor(n);
const toRGBArr = (r: number, g: number, b: number): ColorTuple => [
  toI(r * 256),
  toI(g * 256),
  toI(b * 256),
];

const toHSVLUVArr = (h: number, s: number, l: number): ColorTuple => [
  h * 360,
  s * 100,
  l * 100,
];

type HsvToRgb = (h: number, s: number, v: number) => number[];
type HsvToRgbStr = (h: number, s: number, v: number) => string;

/**
 * HSV color space conversion algo.
 * https://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
 * @param h
 * @param s
 * @param v
 * @returns
 */
const hsvToRgb: HsvToRgb = (h, s, v) => {
  const hInt = toI(h * 6);
  const f = h * 6 - hInt;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let [r, g, b] = [0, 0, 0];

  switch (hInt) {
    case 0:
      [r, g, b] = [v, t, p];
      break;
    case 1:
      [r, g, b] = [q, v, p];
      break;
    case 2:
      [r, g, b] = [p, v, t];
      break;
    case 3:
      [r, g, b] = [p, q, v];
      break;
    case 4:
      [r, g, b] = [t, p, v];
      break;
    case 5:
      [r, g, b] = [v, p, q];
      break;
    default:
      [r, g, b] = [0, 0, 0];
  }
  return toRGBArr(r, g, b);
};

export const hsvToRgbString: HsvToRgbStr = (h, s, v) => {
  const [r, g, b] = hsvToRgb(h, s, v);
  return `rgb(${r}, ${g}, ${b})`;
};

export const okhsvToRgbString: HsvToRgbStr = (h, s, v) => {
  const { r, g, b } = okhsvToSrgb({ h, s, v });
  const [_r, _g, _b] = toRGBArr(r, g, b);
  return `rgb(${_r}, ${_g}, ${_b})`;
};

export const okhslToRgbString: HsvToRgbStr = (h, s, l) => {
  const { r, g, b } = okhslToSrgb({ h, s, l });
  const [_r, _g, _b] = toRGBArr(r, g, b);
  return `rgb(${_r}, ${_g}, ${_b})`;
};

export const hsluvToRgbString: HsvToRgbStr = (h, s, l) => {
  let [r, g, b] = hsluvToRgb([h, s, l]);
  [r, g, b] = toRGBArr(r, g, b);
  return `rgb(${r}, ${g}, ${b})`;
};

export const hsluvToRgbStringScaled: HsvToRgbStr = (h, s, l) => {
  let [r, g, b] = hsluvToRgb(toHSVLUVArr(h, s, l));
  [r, g, b] = toRGBArr(r, g, b);
  return `rgb(${r}, ${g}, ${b})`;
};

export const hsluvToRgbaStringScaled =
  (opacity: number): HsvToRgbStr =>
  (h, s, l) => {
    let [r, g, b] = hsluvToRgb(toHSVLUVArr(h, s, l));
    [r, g, b] = toRGBArr(r, g, b);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

/**
 * Creates a function that will generate colors from a given color space (HSV by default)
 * with a decent perceptual distance without knowing in advance how many colors will
 * be needed.
 *
 * Saturation and value parameters are fixed upon initialisation to ensure a consistent
 * visual relationship between generated colors.
 *
 * The more colors generated by the returned function - the higher the probability that
 * they will get too close to a previous value.
 *
 * Some alternative s,v values:
 *
 * s=0.99, v=0.99
 * s=0.25, h=0.8
 * s=0.3, v=0.99
 *
 * @param h - starting hue value between 0 - 1.  Will be chosen at random if not provided.
 * @param s - the fixed saturation value between 0 - 1.
 * @param v - the fixed value... value between 0 - 1.
 * @param d - the distance between colors.  The default is the golden ration
 * conjugate which has the property of ensuring the colors always fall in the
 * middle of a previous interval if the space between 0 and 1 is exhausted.
 * @param colorSpaceToRGB - a function accept the h,s,v params and returns a
 * converted rgb string.  Use this to apply the sequencing algo to different color
 * spaces.
 * @returns rgb string.
 */
export const makeHSVColorGenerator = (
  h: number | null = null,
  s = 0.5,
  v = 0.95,
  d = 0.618033988749895,
  colorSpaceToRGB = hsvToRgbString,
) => {
  let count = 0;
  const initH = h || Math.random();
  let currH = initH;
  const hValues: number[] = [];

  // Function to hash a string to a number between 0 and 1
  const hashStringToHue = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
      // eslint-disable-next-line
      hash = (hash << 5) - hash + str.charCodeAt(i);
      // eslint-disable-next-line
      hash |= 0; // Convert to 32bit integer
    }
    // eslint-disable-next-line
    return (Math.abs(hash) / 0xffffffff / d + initH) % 1; // Ensure the result is between 0 and 1
  };

  const getColor = (str?: string) => {
    if (str) {
      currH = hashStringToHue(str); // Hash the string to get a unique starting hue
      // currH = (currH + d + initH) % 1; // Apply the golden ratio increment
    } else {
      if (count > 0) {
        currH += d;
        currH %= 1;
      }
      count += 1;
    }
    hValues.push(currH);
    return colorSpaceToRGB(currH, s, v);
  };

  /*
  const getColor = (str: string) => {
    if (count > 0) {
      currH += d;
      currH %= 1;
      hValues.push(currH);
    }
    count += 1;
    return colorSpaceToRGB(currH, s, v);
  };
  */
  getColor.getLastHueValue = () => currH;
  getColor.getHueValues = () => hValues;
  getColor.getHueDifferences = () => {
    const initArr: number[] = [];
    const sortedHues = [...hValues].sort();
    return sortedHues.reduce((acc, curr, index) => {
      if (index === 0) {
        acc.push(0);
      } else {
        acc.push(curr - sortedHues[index - 1]);
      }
      return acc;
    }, initArr);
  };
  getColor.getParams = () => [initH, s, v, d];

  return getColor;
};

makeHSVColorGenerator.defaultArgs = [null, 0.5, 0.95, 0.618033988749895];

export const makeOKHSVColorGenerator: typeof makeHSVColorGenerator = (
  h = 0.484,
  s = 0.85,
  v = 0.926,
  d = 0.618033988749895,
  // d = 0.089,
  colorSpaceToRGB = okhsvToRgbString,
) => {
  const getColor = makeHSVColorGenerator(h, s, v, d, colorSpaceToRGB);

  return getColor;
};

makeOKHSVColorGenerator.defaultArgs = [0.484, 0.85, 0.926, 0.618033988749895];

export const makeOKHSLColorGenerator: typeof makeHSVColorGenerator = (
  h = 0.394,
  s = 1,
  l = 0.7,
  d?: number,
  colorSpaceToRGB = okhslToRgbString,
) => {
  const getColor = makeHSVColorGenerator(h, s, l, d, colorSpaceToRGB);

  return getColor;
};

makeOKHSLColorGenerator.defaultArgs = [0.394, 1, 0.7];

/**
 * Same interface and logic as makeHSVColorGenerator but uses hsluv color space.
 *
 * Additional info:
 * https://www.hsluv.org/
 * https://github.com/hsluv/hsluv/tree/master/javascript
 *
 * An example light pallette args
 * h,
 * s = 0.88,
 * l = 0.8,
 * 
 * darker:
 * 
 * H: 0.7, S: 0.824, L: 0.5252, D: 0.61803398874989
 * 
 * h = 150.6244983382848 / 360,
  s = 81.95173673637682 / 100,
  l = 68.97124610799924 / 100,

    h = 0.7,
  s = 0.867,
  l = 0.522,
 */
export const makeHSLUVColorGenerator: typeof makeHSVColorGenerator = (
  h = 150.6244983382848 / 360,
  s = 81.95173673637682 / 100,
  l = 68.97124610799924 / 100,
  d?: number,
  colorSpaceToRGB = hsluvToRgbStringScaled,
) => {
  const getColor = makeHSVColorGenerator(h, s, l, d, colorSpaceToRGB);

  return getColor;
};

makeHSLUVColorGenerator.defaultArgs = [
  150.6244983382848 / 360,
  81.95173673637682 / 100,
  68.97124610799924 / 100,
];

/**
 * Generate a random hexademical color string. #rrggbb
 */
export const getRandomColor = (): string =>
  `#${getRandomHexadecimal()}${getRandomHexadecimal()}${getRandomHexadecimal()}`;

interface IColorState {
  colorsById: Record<string, string>;
  huesById: Record<string, number>;
}

/**
 * Create a color generator that saves the generated
 * colors and hues to a state object.
 * @param colorState - state object must have colorsById
 * and huesById attrs.  Empty one is init for you by default.
 * @param colorFactory - factory func that returns a color
 * generator.
 * @returns a function that accepts an id.  It will generate
 * a new color and return it if one does not exist for that id,
 * otherwise will return the saved color.
 */
export const makeColorsById = (
  colorState?: IColorState | null,
  colorFactory = makeOKHSVColorGenerator,
  colorSpaceToRGBArg?: boolean,
  colorFactoryArgs: [
    number | undefined,
    number | undefined,
    number | undefined,
    number | undefined,
  ] = [undefined, undefined, undefined, undefined],
) => {
  const { colorsById, huesById } = colorState || {
    colorsById: {},
    huesById: {},
  };
  let colorSpaceToRGB;

  if (!colorSpaceToRGBArg && colorFactory === makeOKHSVColorGenerator) {
    colorSpaceToRGB = okhsvToRgbString;
  }

  if (!colorSpaceToRGBArg && colorFactory === makeOKHSLColorGenerator) {
    colorSpaceToRGB = okhslToRgbString;
  }

  if (!colorSpaceToRGBArg && colorFactory === makeHSLUVColorGenerator) {
    colorSpaceToRGB = hsluvToRgbStringScaled;
  }

  if (!colorSpaceToRGBArg && colorFactory === makeHSVColorGenerator) {
    colorSpaceToRGB = hsvToRgbString;
  }

  const { defaultArgs } = colorFactory;

  const replacedArgs = colorFactoryArgs.map((arg, index) => {
    if (arg === undefined || arg === null) {
      return defaultArgs[index];
    }
    return arg;
  });

  const replacedArgsWithToRgb = [...replacedArgs, colorSpaceToRGB] as [
    number | undefined,
    number | undefined,
    number | undefined,
    number | undefined,
    HsvToRgbStr,
  ];
  const generateColor = colorFactory(...replacedArgsWithToRgb);

  const getColorById = (id: string, useHash = false) => {
    if (!hasOwnProperty(colorsById, id)) {
      colorsById[id] = useHash ? generateColor(id) : generateColor();
      huesById[id] = generateColor.getLastHueValue();
    }
    return colorsById[id];
  };
  getColorById.generateColor = generateColor;
  return getColorById;
};

export const rgbToRgba = (rgb: string, a: number) =>
  rgb.replace(')', `, ${a})`).replace('rgb', 'rgba');

/**
 * Beecastle brand colours
 *
 * FFCC05 - rgb(255,204,5) - yellow - hsluv: [63.54814042639118, 100.00000000000773, 84.20402410364919]
 * 49BC8D - aqua green - hsluv: [150.6244983382848, 81.95173673637682, 68.97124610799924]
 * 00A1E4 - light blue
 * EF5051 - dark pink
 * 000000 - black
 * ECE7E0 - light grey
 * 979797 - dark grey
 */

export const shadeColor = (color: string, percent: number) => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString(), 10);
  G = parseInt(((G * (100 + percent)) / 100).toString(), 10);
  B = parseInt(((B * (100 + percent)) / 100).toString(), 10);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR =
    R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
  const GG =
    G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
  const BB =
    B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);

  return `#${RR}${GG}${BB}`;
};
type RGB = [number, number, number];

export const extractRGB = (rgbString: string): RGB => {
  const regex = /rgb\((-?\d+),\s*(-?\d+),\s*(-?\d+)\)/;
  const match = rgbString.match(regex);

  if (!match) {
    throw new Error(`Invalid RGB string: ${rgbString}`);
  }

  let r = parseInt(match[1], 10);
  let g = parseInt(match[2], 10);
  let b = parseInt(match[3], 10);

  r = r < 0 ? 0 : r;
  g = g < 0 ? 0 : g;
  b = b < 0 ? 0 : b;

  return [r, g, b];
};

export const calculateLuminance = ([r, g, b]: RGB): number => {
  const [R, G, B] = [r, g, b].map((color) => {
    const normalized = color / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export const determineTextColor = (rgb: RGB): string => {
  return calculateLuminance(rgb) > 0.179 ? 'rgb(24, 39, 57)' : '#ffffff';
};

export const hexToRgb = (
  hex: string,
): { r: number; g: number; b: number } | null => {
  // Remove the hash at the beginning if it's there
  const hexReplaced = hex.replace(/^#/, '');

  // Ensure it's 6 digits
  if (hexReplaced.length !== 6) {
    return null;
  }

  // Split the hex string into three parts (r, g, b)
  const r = parseInt(hexReplaced.substring(0, 2), 16);
  const g = parseInt(hexReplaced.substring(2, 4), 16);
  const b = parseInt(hexReplaced.substring(4, 6), 16);

  return { r, g, b };
};

export const hexToRgbStr = (hex: string) => {
  const { r, g, b } = hexToRgb(hex) || { r: '', g: '', b: '' };
  return `rgb(${r}, ${g}, ${b})`;
};
